/**
 * AI Dosimetry Workflow Module.
 *
 * Exports the workflow configuration, services, and components for liver dosimetry planning.
 *
 * WIP — The following features are not yet complete:
 * - PDF export (report step has a disabled button)
 * - Hover highlighting from sidebar to 3D mesh opacity
 */

import { DosimetryPlanningPanel } from './components/dosimetry-planning-panel';
import { DosimetryReportPanel } from './components/dosimetry-report-panel';
import { DosimetrySaveStatus } from './components/dosimetry-save-status';
import { DosimetrySegmentationPanel } from './components/dosimetry-segmentation-panel';
import { DOSIMETRY_STEP, DOSIMETRY_FEATURE_ID } from './dosimetry-constants';
import { LAYOUT_QUAD } from '../../common/layouts';
import { VISIBLE_2D_ONLY, VISIBLE_3D_ONLY } from '../../common/visibility-helpers';
import { createWorkflowConfig, IFeatureVisibilityRule } from '../../types';
import { DosimetryReportPreview } from './components/report/dosimetry-report-preview';

// ============================================================================
// Visibility Rules
// ============================================================================

/**
 * Declarative visibility rules for the AI Dosimetry workflow:
 *
 * - Step "segmentation": structure sets visible, TEP overlay hidden
 * - Step "dosimetry" with TEP feature present: structure sets hidden, TEP overlay visible
 * - Step "dosimetry" without TEP feature: structure sets visible
 * - Step "report": no viewports rendered, visibility doesn't matter
 */
export const DOSIMETRY_FEATURE_VISIBILITY: readonly IFeatureVisibilityRule[] = [
    {
        featureId: DOSIMETRY_FEATURE_ID.SEGMENTS,
        isVisible: ({ stepId, hasFeature }) => {
            if (stepId === DOSIMETRY_STEP.SEGMENTATION) return true;
            if (stepId === DOSIMETRY_STEP.DOSIMETRY) {
                return hasFeature(DOSIMETRY_FEATURE_ID.TEP_OVERLAY) ? VISIBLE_3D_ONLY : true;
            }
            if (stepId === DOSIMETRY_STEP.REPORT) return true;
            return false;
        },
    },
    {
        featureId: DOSIMETRY_FEATURE_ID.TUMORS,
        isVisible: ({ stepId, hasFeature }) => {
            if (stepId === DOSIMETRY_STEP.SEGMENTATION) return true;
            if (stepId === DOSIMETRY_STEP.DOSIMETRY) {
                return hasFeature(DOSIMETRY_FEATURE_ID.TEP_OVERLAY) ? VISIBLE_3D_ONLY : true;
            }
            return false;
        },
    },
    {
        featureId: DOSIMETRY_FEATURE_ID.TEP_OVERLAY,
        isVisible: ({ stepId }) => (stepId === DOSIMETRY_STEP.DOSIMETRY ? VISIBLE_2D_ONLY : false),
    },
];

// ============================================================================
// Workflow Config
// ============================================================================

export const DOSIMETRY_WORKFLOW_CONFIG = createWorkflowConfig({
    workflowId: 'ai-dosimetry',
    layouts: [LAYOUT_QUAD],
    panels: {},
    headerExtra: DosimetrySaveStatus,
    featureVisibility: DOSIMETRY_FEATURE_VISIBILITY,
    defaultWindowLevelPresetId: 'liver',
    steps: [
        {
            id: DOSIMETRY_STEP.SEGMENTATION,
            title: '1 - Segmentation',
            sidebar: DosimetrySegmentationPanel,
            mainView: { type: 'layout', layoutName: 'quad' },
        },
        {
            id: DOSIMETRY_STEP.DOSIMETRY,
            title: '2 - Dosimetry',
            sidebar: DosimetryPlanningPanel,
            mainView: { type: 'layout', layoutName: 'quad' },
        },
        {
            id: DOSIMETRY_STEP.REPORT,
            title: '3 - Report',
            sidebar: DosimetryReportPanel,
            mainView: { type: 'component', component: DosimetryReportPreview },
        },
    ],
    defaultStepId: 'segmentation',
});

export { DosimetryWorkflowService } from './services/dosimetry-workflow-service';
